import React from 'react';
import { Tooltip } from 'antd';
import { IoMdSearch } from 'react-icons/io';
import type { FinancialRiskResult } from 'pages/FarmsPage/Workspace/Insights/computeFinancialRisk';
import BreakEvenChart, {
  PRICE_LABELS,
  PRICE_LINE_COLORS,
} from './BreakEvenChart';
import { ChartEmptyState } from './PlanActualChartControls';

/*
 * Break-even yield/price rows and the break-even chart + legend, shared by the
 * Live totals Financial risk panel and the Insights "Margin & break-even" lens
 * (OP-2393). Both render straight from one computeFinancialRisk result, so the
 * two views can never disagree for the same scope and basis.
 */

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/**
 * Formats a dollar amount compactly with sign.
 * Negative values use the "−" minus sign (not a hyphen).
 */
export const formatDollar = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(2)}k`;
  return `${sign}$${abs.toFixed(2)}`;
};

/**
 * Formats a dollar amount to full precision (dollars and cents) for tooltips.
 * e.g. −$6,332,456.78
 */
export const formatDollarFull = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  return `${sign}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Shortens a handful of common long-form unit names (e.g. "Tonnes" → "t") for
 * display, matching how Quantity Required already shows its own units.
 * risk.yieldUnit is a free-text snapshot from the org's unit catalogue, so an
 * unrecognised string (e.g. "Bales") passes through unchanged.
 */
const UNIT_ABBREVIATIONS: Record<string, string> = {
  tonnes: 't',
  tonne: 't',
  kilograms: 'kg',
  kilogram: 'kg',
  litres: 'L',
  litre: 'L',
  liters: 'L',
  liter: 'L',
  hectares: 'ha',
  hectare: 'ha',
  grams: 'g',
  gram: 'g',
};
export const abbreviateUnit = (unit: string): string =>
  UNIT_ABBREVIATIONS[unit.trim().toLowerCase()] ?? unit;

/** Alternating row shading — bg-white / bg-gray-50, same convention as the other Live totals tables. */
const rowShade = (idx: number): string =>
  idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

const PRICE_SCENARIO_LABELS = PRICE_LABELS.map((l) => `${l} price`);
const YIELD_SCENARIO_LABELS = PRICE_LABELS.map((l) => `${l} yield`);

/* ── Sub-components ─────────────────────────────────────────────────────── */

/**
 * Small SVG line swatch used in the chart legend. Avoids the `style` prop by
 * setting colour via SVG presentation attributes.
 */
const ScenarioLine: React.FC<{ color: string; dashed?: boolean }> = ({
  color,
  dashed,
}) => (
  <svg
    width="16"
    height="3"
    viewBox="0 0 16 3"
    aria-hidden="true"
    className="flex-shrink-0"
  >
    <line
      x1="0"
      y1="1.5"
      x2="16"
      y2="1.5"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeDasharray={dashed ? '4 2' : undefined}
    />
  </svg>
);

/**
 * Shown next to a section header when inputs were excluded from the
 * yield/price aggregation due to a mismatched yield unit.
 */
const SkippedIcon: React.FC<{ count: number; unit: string }> = ({
  count,
  unit,
}) => (
  <Tooltip
    title={
      <>
        <div>Yield unit mismatch:</div>
        <div>
          {count} input{count > 1 ? 's' : ''} excluded (unit ≠ {unit}).
        </div>
        <div>Break-even figures cover matching inputs only.</div>
      </>
    }
  >
    <span className="inline-flex items-center align-middle">
      <IoMdSearch
        className="ml-1 cursor-help text-gray-medium transition-transform hover:scale-150"
        size={12}
      />
    </span>
  </Tooltip>
);

const EmptyValue: React.FC = () => <span className="text-gray-muted">—</span>;

/** A break-even yield value (number + yield-unit caption), or a dash when invalid. */
const YieldCell: React.FC<{ value: number; unit: string }> = ({
  value,
  unit,
}) =>
  value > 0 ? (
    <span className="whitespace-nowrap">
      {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      <span className="ml-1 text-xs font-semibold text-gray-muted">
        {abbreviateUnit(unit)}/ha
      </span>
    </span>
  ) : (
    <EmptyValue />
  );

/** A break-even price value ($ + /unit caption), or a dash when invalid. */
const PriceCell: React.FC<{ value: number; unit: string }> = ({
  value,
  unit,
}) =>
  value > 0 ? (
    <span className="whitespace-nowrap">
      {formatDollar(value)}
      <span className="ml-1 text-xs font-semibold text-gray-muted">
        /{abbreviateUnit(unit)}
      </span>
    </span>
  ) : (
    <EmptyValue />
  );

/**
 * A break-even scenario row's label (e.g. "Low price") with a small caption
 * below naming the actual reference figure that scenario assumed (e.g.
 * "@ $220.00/t") — the "Low"/"Expected"/"High" label alone doesn't say what
 * that price or yield actually was.
 */
const ScenarioLabel: React.FC<{ label: string; refValue: string }> = ({
  label,
  refValue,
}) => (
  <span className="inline-flex flex-col">
    <span>{label}</span>
    <span className="whitespace-nowrap text-xs font-semibold text-gray-muted">
      @ {refValue}
    </span>
  </span>
);

/** Section heading row — excluded from the stripe, underlined so it reads as the heading for the rows beneath it. */
const SectionHeadingRow: React.FC<{
  title: string;
  skippedUnitCount: number;
  unit: string;
}> = ({ title, skippedUnitCount, unit }) => (
  <tr>
    <td
      colSpan={2}
      className="border-b pb-1 pr-3 pt-3 text-sm font-semibold text-secondary-dark"
    >
      {title}
      {skippedUnitCount > 0 && (
        <SkippedIcon count={skippedUnitCount} unit={unit} />
      )}
    </td>
  </tr>
);

/* ── Exports ─────────────────────────────────────────────────────────────── */

interface BreakEvenRowsProps {
  risk: FinancialRiskResult;
  /**
   * How many striped data rows the parent table has already rendered above
   * these, so the alternating shading stays continuous across the table.
   */
  stripeOffset?: number;
}

/**
 * The Break-even yield and Break-even price sections as `<tr>` rows, for use
 * inside a two-column `<table><tbody>`. Scenario rows with no reference
 * price/yield are skipped rather than shown as a dash.
 */
export const BreakEvenRows: React.FC<BreakEvenRowsProps> = ({
  risk,
  stripeOffset = 0,
}) => {
  let stripe = stripeOffset;
  const nextShade = () => rowShade(stripe++);

  const hasBreakEven = risk.totalRevenueArea > 0;
  const unit = abbreviateUnit(risk.yieldUnit);

  // Called in render order so the stripe runs continuously down the table.
  const emptyRow = () => (
    <tr className={nextShade()}>
      <td colSpan={2} className="py-1 pr-3 text-label text-secondary-dark">
        —
      </td>
    </tr>
  );

  const yieldRows = [0, 1, 2].map((i) => {
    const price = risk.weightedRefPrices[i] ?? 0;
    if (!hasBreakEven || price <= 0) return null;
    return (
      <tr key={`bey-${i}`} className={nextShade()}>
        <td className="py-1 pr-3 text-label text-charcoal">
          <ScenarioLabel
            label={PRICE_SCENARIO_LABELS[i]}
            refValue={`${formatDollar(price)}/${unit}`}
          />
        </td>
        <td className="py-1 text-right tabular-nums text-charcoal">
          <YieldCell
            value={risk.breakEvenYields[i] ?? 0}
            unit={risk.yieldUnit}
          />
        </td>
      </tr>
    );
  });
  const yieldEmpty = hasBreakEven ? null : emptyRow();

  const priceRows = [0, 1, 2].map((i) => {
    const yieldVal = risk.weightedRefYields[i] ?? 0;
    if (!hasBreakEven || yieldVal <= 0) return null;
    return (
      <tr key={`bep-${i}`} className={nextShade()}>
        <td className="py-1 pr-3 text-label text-charcoal">
          <ScenarioLabel
            label={YIELD_SCENARIO_LABELS[i]}
            refValue={`${yieldVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}/ha`}
          />
        </td>
        <td className="py-1 text-right tabular-nums text-charcoal">
          <PriceCell
            value={risk.breakEvenPrices[i] ?? 0}
            unit={risk.yieldUnit}
          />
        </td>
      </tr>
    );
  });
  const priceEmpty = hasBreakEven ? null : emptyRow();

  return (
    <>
      <SectionHeadingRow
        title="Break-even yield"
        skippedUnitCount={risk.skippedUnitCount}
        unit={unit}
      />
      {yieldRows}
      {yieldEmpty}
      <SectionHeadingRow
        title="Break-even price"
        skippedUnitCount={risk.skippedUnitCount}
        unit={unit}
      />
      {priceRows}
      {priceEmpty}
    </>
  );
};

/** True when the risk result has enough data to draw the break-even chart. */
export const canShowBreakEvenChart = (risk: FinancialRiskResult): boolean =>
  risk.totalSpend > 0 &&
  risk.totalRevenueArea > 0 &&
  risk.weightedRefPrices.some((p) => p > 0) &&
  risk.weightedRefYields.some((y) => y > 0);

/**
 * The break-even chart with its legend below it, or the shared chart empty
 * state when there isn't enough production and cost data to draw it.
 */
export const BreakEvenChartWithLegend: React.FC<{
  risk: FinancialRiskResult;
}> = ({ risk }) => {
  if (!canShowBreakEvenChart(risk)) return <ChartEmptyState />;

  return (
    <>
      <div>
        <BreakEvenChart
          totalSpend={risk.totalSpend}
          totalRevenueArea={risk.totalRevenueArea}
          refYields={risk.weightedRefYields}
          refPrices={risk.weightedRefPrices}
          yieldUnit={risk.yieldUnit}
        />
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 px-2 text-base text-secondary-dark">
        {risk.weightedRefPrices.map((p, i) =>
          p > 0 ? (
            <span key={i} className="flex items-center gap-1">
              <ScenarioLine color={PRICE_LINE_COLORS[i]} />
              {PRICE_SCENARIO_LABELS[i]}
            </span>
          ) : null
        )}
        {risk.totalSpend > 0 && (
          <span className="flex items-center gap-1">
            <ScenarioLine color="var(--color-danger)" dashed />
            Cost
          </span>
        )}
      </div>
    </>
  );
};
